import React, { useCallback, useMemo } from 'react';

import useToggle from '@lyearn/core/hooks/useToggle';
import { useTranslation } from '@lyearn/i18n';
import { Image } from '@lyearn/icons';
import { cn } from '@lyearn/style';
import { Box, Button, Stack } from '@lyearn/ui';
import BannerWithTitle from '@/components/EditorBuilder/BannerWithTitle';
import { CoverProps } from '@/components/EditorBuilder/BannerWithTitle/types';
import { bannerButtonStyles } from '@/components/EditorBuilder/styles';
import DatabaseFormEditor from '@/modules/corporate/modules/database/views/FormView/components/DatabaseFormEditor';
import { DatabaseFormEditorActionEnum } from '@/modules/corporate/modules/database/views/FormView/components/DatabaseFormEditor/types';
import { SurveyEditorActionEnum } from '@/modules/corporate/modules/survey/views/SurveyEditor/hooks/useSurveyEditor/types';
import { WELCOME_BLOCK_ID } from '@/modules/corporate/modules/survey/views/SurveyEditor/types';
import { DatabaseFieldTypeEnum, MediaKindEnum, UpdateSurveyInput } from '@/types/schema';

import useSurveyEditor from '../../hooks/useSurveyEditor';
import AddSurveyBlock from '../AddSurveyBlock';
import SurveyWelcomeBlockEditor from './components/SurveyWelcomeBlockEditor';
import { surveyBannerStyles, surveyEditorContentStyles } from './styles';

const ADD_SURVEY_BUTTON_CLASSES = {
  label: 'text-text-placeholder',
};

type UseSurveyEditorReturnType = ReturnType<typeof useSurveyEditor>;

type SurveyEditorContentProps = Pick<
  UseSurveyEditorReturnType,
  'adaptedFields' | 'isFieldPublished' | 'uiState' | 'onAction'
> & {
  survey: UpdateSurveyInput;
  getFieldAttributes: (fieldId: string) => { index: number; descriptionEnabled: boolean };
  className?: string;
};

function SurveyEditorContent(props: SurveyEditorContentProps) {
  const { t } = useTranslation();
  const {
    adaptedFields,
    isFieldPublished,
    uiState,
    survey,
    onAction,
    className,
    getFieldAttributes,
  } = props;
  const [showCoverDialog, { toggle: toggleCoverDialog }] = useToggle(false);
  const coverUrl = survey.cover?.url || '';
  const coverOffset = survey.coverOffset || 0;

  const onAddBlock = useCallback(
    (dataType: string) => {
      onAction({
        type: SurveyEditorActionEnum.AddField,
        payload: { type: dataType as DatabaseFieldTypeEnum },
      });
    },
    [onAction],
  );

  const { onSurveyChange, onWelcomeBlockClick } = useMemo(
    () => ({
      onWelcomeBlockClick: () => {
        onAction({
          type: DatabaseFormEditorActionEnum.SelectField,
          payload: { fieldId: WELCOME_BLOCK_ID },
        });
      },
      onSurveyChange: (updates: Partial<UpdateSurveyInput>) => {
        onAction({ type: SurveyEditorActionEnum.UpdateSurvey, payload: { updates } });
      },
    }),
    [onAction],
  );

  const onCoverSave = useCallback(
    ({ yOffsetPercent, picture }: CoverProps) => {
      onSurveyChange({
        coverOffset: yOffsetPercent,
        cover: picture ? { name: 'Cover', kind: MediaKindEnum.Image, url: picture } : null,
      });
    },
    [onSurveyChange],
  );

  return (
    <Box className={cn('flex-1 overflow-auto bg-bg-secondary', className)}>
      <BannerWithTitle
        canRemoveBanner={false}
        className={surveyBannerStyles}
        cover={{ src: coverUrl, yOffsetPercent: coverOffset }}
        showCoverDialog={showCoverDialog}
        toggleCoverDialog={toggleCoverDialog}
        onCoverSave={onCoverSave}
      />
      <Stack
        className={cn('relative z-20 mx-auto mb-64 mt-28', surveyEditorContentStyles)}
        direction="col">
        {!coverUrl ? (
          <Stack alignItems="center" className="group mb-4 -ms-4">
            <Button
              className={cn('opacity-0 group-hover:opacity-100', bannerButtonStyles)}
              classes={ADD_SURVEY_BUTTON_CLASSES}
              shape="square"
              size="small"
              startIcon={<Image fillColorToken="icon-placeholder" />}
              variant="ghost"
              onClick={toggleCoverDialog}>
              {t('SurveyEditor:AddSurveyCover')}
            </Button>
          </Stack>
        ) : null}
        <SurveyWelcomeBlockEditor
          error={uiState.errors[WELCOME_BLOCK_ID]}
          isSelected={uiState.selectedBlockId === WELCOME_BLOCK_ID}
          survey={survey}
          onChange={onSurveyChange}
          onClick={onWelcomeBlockClick}
        />
        <DatabaseFormEditor
          errors={uiState.errors}
          fields={adaptedFields}
          getFieldAttributes={getFieldAttributes}
          isFieldPublished={isFieldPublished}
          selectedFieldId={uiState.selectedBlockId}
          onFormEditorAction={onAction}
        />
        <AddSurveyBlock onAddBlock={onAddBlock} />
      </Stack>
    </Box>
  );
}

export default SurveyEditorContent;
